import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@/components/atoms';
import { useDispatch, useSelector } from 'react-redux';
import { ILineItemMutation } from '@/types/ILineItemMutation';
import { multipleAddToCart } from '@/store/actions/cart';
import { RootState } from '@/store/store';
import { isCartValid } from '@/utils/Cart';
import { closeSnackbar, openModal, openSnackbar } from '@/store/actions/ui';
import { useEffect } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { AddToCartPayload } from '@/utils/Impressions';

interface AddVariantsToCartProps {
  chosenVariants: { [variantId: string]: number };
}

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const CustomTypographyLink = styled(Typography)`
  border-bottom-color: ${props => props.theme.paletteDefault.primary.main};
  border-bottom-width: 1.5px;
  border-bottom-style: solid;
  font-weight: 700;
  &:hover {
    color: ${props => props.theme.paletteDefault.primary.main};
  }
`;

const CustomButton = styled(Button)`
  width: 100%;
`;

const AddVariantsToCart: React.FC<AddVariantsToCartProps> = props => {
  const { chosenVariants } = props;
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const cartReducer = useSelector((state: RootState) => state.cartReducer);
  const currency = useSelector(
    (state: RootState) => state.currencyReducer.currency
  );
  const isLogged = useSelector(
    (state: RootState) => state.userReducer.isLogged
  );
  const customer = useSelector(
    (state: RootState) => state.userReducer.customer
  );

  const lineItems: ILineItemMutation[] = Object.entries(chosenVariants).map(
    item => ({
      variantId: item[0],
      quantity: item[1],
    })
  );

  // is it possible to avoid creating the custom event and use "then" after the "add to cart" promise?
  useEffect(() => {
    document.addEventListener('addedToCart', addToCartEventHandler);
    return () => {
      document.removeEventListener('addedToCart', addToCartEventHandler);
      dispatch(closeSnackbar());
    };
  }, []);

  const addToCartEventHandler = (e: CustomEvent<AddToCartPayload>) => {
    const productTitle = e.detail.lineItems[0]?.title;
    const addedQuantity = e.detail.qty;
    dispatch(
      openSnackbar(
        'success',
        { vertical: 'bottom', horizontal: 'center' },
        <MessageWrapper>
          <Typography component="span" datatype="body_1">
            {`${addedQuantity} ${productTitle} ${t(
              'common:cart.add-cart.desc-1'
            )} `}
            <Link href={'/cart'} passHref>
              <a onClick={() => dispatch(closeSnackbar())}>
                <CustomTypographyLink component="span" datatype="body_1">
                  {t('common:cart.label').toUpperCase()}
                </CustomTypographyLink>
              </a>
            </Link>
          </Typography>
        </MessageWrapper>
      )
    );
  };

  const onAddToCart = (): void => {
    if (currency && isCartValid(cartReducer.cart)) {
      dispatch(closeSnackbar());
      dispatch(multipleAddToCart(lineItems, currency, customer));
    } else {
      dispatch(openModal('cartInvalid'));
    }
  };

  return (
    <CustomButton
      onClick={!isLogged ? () => dispatch(openModal('register')) : onAddToCart}
      disabled={cartReducer.loadingCart || lineItems.length < 1}
      loading={cartReducer.loadingCart}>
      {t('cart.add-cart.title')}
    </CustomButton>
  );
};

export default AddVariantsToCart;
